import { convertTokens, legacyParse } from '@date-fns/upgrade/v2';
import {
  Button,
  FormLabel,
  Grid,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import AssignmentIcon from '@mui/icons-material/Assignment';
import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import PropTypes from 'prop-types';
import React, { useEffect } from 'react';
import Helmet from 'react-helmet';
import { connect, useSelector } from 'react-redux';
import { Link, useLocation, withRouter } from 'react-router-dom';
import { bindActionCreators, compose } from 'redux';

import CompletedIcon from 'assets/icons/completed.svg';
import { ViewInAppButton } from 'components/ViewInAppButton';
import ProfileIcon from 'assets/icons/profile-colored.svg';
import PublishedIcon from 'assets/icons/published.svg';
import config from 'config';
import * as NotificationActions from 'containers/Notification/actions';
import Api from 'entities/api';
import { selectFormById } from 'entities/form/selectors';
import { selectOrganization, selectVolunteerRewardValidationForm } from 'entities/user/selectors';
import Box, { TableBox } from 'retired/Box';
import DialogButton from 'retired/DialogButton';
import FormResponsesAggregation from 'retired/FormResponsesAggregation';
import Img from 'retired/Img';
import { getAppDomain } from 'utils/getAppDomain';
import { useInjectEpics } from 'utils/injectEpics';
import { useInjectReducer } from 'utils/injectReducer';

import FormResponseOverlay from './FormResponseOverlay';
import * as Actions from './actions';
import epics from './epics';
import reducer from './reducer';
import { selectError, selectFormResponseOverlayId, selectLoading } from './selectors';

const key = 'formPage';

const FormPage = ({ loading, form, error, actions, match, formResponseOverlayId }) => {
  const location = useLocation();
  const isSurvey = location.pathname.startsWith('/surveys');
  useInjectReducer({ key, reducer });
  useInjectEpics({ key, epics });
  useEffect(() => {
    actions.initAction(match.params.id);
  }, []);
  const organization = useSelector(selectOrganization);
  const volunteerRewardValidationFormId = useSelector(selectVolunteerRewardValidationForm);

  if (error) {
    return <h2>There was an error loading forms</h2>;
  }
  if (loading || !form || !form.questions) {
    return <LinearProgress />;
  }

  const isFormOwner = form.get('organization') === organization?.id;
  const isValidationForm = form.id === volunteerRewardValidationFormId;

  const editButton =
    isFormOwner &&
    (form.responsesCount > 0 ? (
      <Button
        color="primary"
        variant="contained"
        disabled
        title={`Can't edit the ${isSurvey ? 'survey' : 'form'} that has responses`}
      >
        Editing locked
      </Button>
    ) : (
      <Link to={`/${isSurvey ? 'surveys' : 'forms'}/edit/${form.id}`}>
        <Button color="primary" variant="contained" title={`Edit ${isSurvey ? 'survey' : 'form'}`}>
          Edit&nbsp;the&nbsp;{isSurvey ? 'survey' : 'form'}
        </Button>
      </Link>
    ));
  const deleteButton = isFormOwner && !isValidationForm && (
    <DialogButton
      style={{
        display: 'inline-block',
        paddingLeft: 20,
        textDecorationLine: 'underline',
        textUnderlinePosition: 'under',
      }}
      asLink
      buttonLabel={<>Delete&nbsp;{isSurvey ? 'survey' : 'form'}</>}
      title={`Delete ${isSurvey ? 'survey' : 'form'}`}
      text={`Are you sure you want to remove this ${isSurvey ? 'survey' : 'form'}?`}
      confirmLabel="Delete"
      cancelLabel="Go Back"
      handleConfirm={() => actions.removeAction(form.id, isSurvey)}
    />
  );
  const previewButton = (
    <ViewInAppButton
      href={`${getAppDomain([organization])}/${isSurvey ? 'surveys' : 'forms'}/${form.get('id')}`}
      text={`Preview the ${isSurvey ? 'survey' : 'form'}`}
      style={{
        textDecorationLine: 'underline',
        padding: 20,
        textUnderlinePosition: 'under',
      }}
    />
  );

  const csvDownloadBtn = (
    // eslint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/click-events-have-key-events
    <a
      style={{
        outline: 'none',
        textDecorationLine: 'underline',
        textUnderlinePosition: 'under',
      }}
      onClick={(e) => {
        const a = e.target.tagName === 'A' ? e.target : e.target.parentElement;
        if (!a.href) {
          fetch(`${config.apiUrl}/api/forms/${form.id}/responsesExport`, {
            method: 'GET',
            headers: {
              Authorization: Api.defaultHeaders.Authorization,
            },
          })
            .then((response) => response.blob())
            .then((blob) => {
              const url = window.URL.createObjectURL(blob);
              a.href = url;
              a.download = `${form.name}-responses.csv`;
              a.click();
              window.URL.revokeObjectURL(url);
              a.removeAttribute('href');
            });
        }
      }}
      role="button"
      tabIndex={0}
      title="Export form results"
    >
      Export Responses{' '}
    </a>
  );

  return (
    <>
      <Helmet title={`${form.name}${form.subtitle ? ` (${form.subtitle})` : ''}`} />
      <FormResponseOverlay
        formResponse={form.responses?.find(
          (formResponse) => formResponse.get('_id') === formResponseOverlayId
        )}
        formQuestions={form.questions}
        toggleAction={actions.toggleFormResponseOverlayAction}
      />
      <div
        style={{
          display: 'flex',
          flexFlow: 'row wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <Typography type="headline" component="h2" style={{ fontSize: 30, fontWeight: 1000 }}>
          {form.name}
        </Typography>
        {/* Various links for managing the deed */}
        <Typography
          component="div"
          align="right"
          style={{
            marginBottom: 5,
          }}
        >
          {csvDownloadBtn}

          {deleteButton}

          {previewButton}

          {editButton}
        </Typography>
      </div>
      <Grid container spacing={3} style={{ position: 'relative' }}>
        <Grid item xs={12}>
          <Box>
            {form.subtitle ? (
              <Typography
                type="headline"
                component="h3"
                style={{ fontSize: 18, fontWeight: 400, marginBottom: 15 }}
              >
                {form.subtitle}
              </Typography>
            ) : null}
            <Typography
              component="div"
              style={{
                fontSize: 15,
                lineHeight: 1.2,
                fontWeight: 300,
                marginTop: 15,
                marginBottom: 15,
              }}
            >
              <FormLabel>{isSurvey ? 'Survey' : 'Form'} link:</FormLabel> <br />
              <TextField
                style={{ width: '100%' }}
                value={`${config.clientUrl}/${isSurvey ? 'surveys' : 'forms'}/${form.get('id')}`}
                onFocus={(event) => {
                  event.target.select();
                }}
              />
            </Typography>
            <Typography
              component="div"
              style={{
                fontSize: 15,
                lineHeight: 1.2,
                fontWeight: 300,
                marginTop: 20,
              }}
            >
              <FormLabel>Number of questions:</FormLabel> {form.questions.size}
            </Typography>
            {!isSurvey && (
              <Typography
                component="div"
                style={{
                  fontSize: 15,
                  lineHeight: 1.2,
                  fontWeight: 300,
                  marginTop: 15,
                }}
              >
                <FormLabel>Number of deeds:</FormLabel> {form.deeds?.size || 0}
              </Typography>
            )}
            <Typography
              component="div"
              style={{
                fontSize: 15,
                lineHeight: 1.2,
                fontWeight: 300,
                marginTop: 15,
              }}
            >
              <FormLabel>Number of responses:</FormLabel> {form.responsesCount}
            </Typography>

            {form.deeds?.size > 0 && (
              <>
                <Typography
                  component="p"
                  style={{
                    fontSize: 17,
                    fontWeight: 500,
                    marginTop: 20,
                  }}
                >
                  Deeds
                </Typography>
                <TableBox style={{ marginTop: 10 }}>
                  <Table size="small" className="colored colored2">
                    <TableHead>
                      <TableRow>
                        <TableCell>Deed</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Users</TableCell>
                        <TableCell>Share form</TableCell>
                        <TableCell>Responses</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {form.deeds.map((deed) => (
                        <TableRow key={deed.get('_id')}>
                          <TableCell style={{ paddingLeft: 40 }}>
                            <Img
                              src={deed.get('ended') ? CompletedIcon : PublishedIcon}
                              alt=""
                              style={{
                                height: 18,
                                position: 'absolute',
                                marginLeft: -30,
                                marginTop: 1,
                              }}
                            />
                            <Link to={`/deeds/events/${deed.get('_id')}`}>{deed.get('name')}</Link>
                          </TableCell>
                          <TableCell>
                            {deed.get('startingAt')
                              ? formatInTimeZone(
                                  deed.get('startingAt'),
                                  deed.get('timeZone'),
                                  convertTokens('dddd MMMM DD, h:mm A')
                                )
                              : ''}
                            {deed.get('endingAt')
                              ? ` - ${formatInTimeZone(
                                  deed.get('endingAt'),
                                  deed.get('timeZone'),
                                  convertTokens('h:mm A')
                                )}`
                              : deed.get('startingAt')
                              ? ' - Ongoing'
                              : ''}
                          </TableCell>
                          <TableCell>
                            {deed.get('attendees')?.size || deed.get('userCount')}
                          </TableCell>
                          <TableCell>
                            <ViewInAppButton
                              href={`${getAppDomain([organization])}/${
                                isSurvey ? 'surveys' : 'forms'
                              }/${form.id}?deed=${deed.get('_id')}`}
                              iconSize={18}
                              style={{
                                display: 'inline-block',
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            {/* eslint-disable-next-line jsx-a11y/anchor-is-valid, jsx-a11y/click-events-have-key-events */}
                            <a
                              style={{
                                outline: 'none',
                                textDecorationLine: 'underline',
                                textUnderlinePosition: 'under',
                              }}
                              onClick={(e) => {
                                const a =
                                  e.target.tagName === 'A' ? e.target : e.target.parentElement;
                                if (!a.href) {
                                  fetch(
                                    `${config.apiUrl}/api/deeds/${deed.get('_id')}/responsesExport`,
                                    {
                                      method: 'GET',
                                      headers: {
                                        Authorization: Api.defaultHeaders.Authorization,
                                      },
                                    }
                                  )
                                    .then((response) => response.blob())
                                    .then((blob) => {
                                      const url = window.URL.createObjectURL(blob);
                                      a.href = url;
                                      a.download = `${deed.get('name')}-responses.csv`;
                                      a.click();
                                      window.URL.revokeObjectURL(url);
                                      a.removeAttribute('href');
                                    });
                                }
                              }}
                              role="button"
                              tabIndex="0"
                              title="Export form results"
                            >
                              Export
                            </a>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableBox>
              </>
            )}
            {form.responses?.size > 0 && (
              <>
                <Typography component="h3" style={{ fontSize: 18, fontWeight: 400, marginTop: 15 }}>
                  Responses
                </Typography>
                <TableBox style={{ marginTop: 10, marginBottom: 0 }}>
                  <Table size="small" className="colored colored3">
                    <TableHead>
                      <TableRow>
                        <TableCell>Name</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Response</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {form.responses.map((response) => (
                        <TableRow key={response.get('_id')}>
                          <TableCell style={{ paddingLeft: 40 }}>
                            <Img
                              src={ProfileIcon}
                              alt=""
                              style={{
                                height: 18,
                                position: 'absolute',
                                marginLeft: -30,
                              }}
                            />
                            {response.get('user') ? (
                              <Link to={`/volunteers/${response.get('user')?.get('id')}`}>
                                {response.get('user')?.get('name')}
                              </Link>
                            ) : (
                              'Anonymous User'
                            )}
                          </TableCell>
                          <TableCell>
                            {response.get('modifiedAt')
                              ? format(
                                  legacyParse(response.get('modifiedAt')),
                                  convertTokens('dddd MMMM DD, h:mm A')
                                )
                              : 'N/A'}
                          </TableCell>
                          <TableCell>
                            <IconButton
                              onClick={() =>
                                actions.toggleFormResponseOverlayAction(response.get('_id'))
                              }
                              size="small"
                              title="Attendee's form response"
                              color="primary"
                            >
                              <AssignmentIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableBox>
              </>
            )}
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              {form.document ? (
                <Box style={{ marginBottom: 20 }}>
                  <a href={form.document} target="_blank">
                    <img
                      style={{
                        width: '100%',
                        objectFit: 'contain',
                      }}
                      src={form.document}
                      alt=""
                    />
                  </a>
                </Box>
              ) : null}
            </Grid>
          </Grid>

          {form.responsesAggregation.length > 0 && (
            <Box>
              <Typography type="headline" component="h3" style={{ fontSize: 18, fontWeight: 400 }}>
                Summary of responses
              </Typography>
              <FormResponsesAggregation responsesAggregation={form.responsesAggregation} />
            </Box>
          )}
        </Grid>
      </Grid>
    </>
  );
};

FormPage.propTypes = {
  loading: PropTypes.bool,
  error: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  form: PropTypes.object,
  actions: PropTypes.object,
  match: PropTypes.object,
  formResponseOverlayId: PropTypes.string,
};

const withConnect = connect(
  (state, props) => ({
    form: selectFormById(state, props.match.params.id),
    loading: selectLoading(state),
    error: selectError(state),
    formResponseOverlayId: selectFormResponseOverlayId(state),
  }),
  (dispatch) => ({
    actions: bindActionCreators({ ...Actions, ...NotificationActions }, dispatch),
  })
);

export default compose(withRouter, withConnect)(FormPage);
